/*
  Ticket-import: in-memory grouping and classification of the parsed file
  rows. These helpers convert flat per-row data into PNR-grouped tickets and
  trip groups, and classify tickets into eligible / cancellation / ignored
  buckets without ever talking to the server.
*/
import { isValidArray, isValidArrayWithValues, isValidString } from '../../utils/utilities';
import { MANDATORY_ROW_FIELDS } from './constants';
import {
    cleanSeatNumber,
    getRowMissingMandatoryFields,
    inferGender,
    isActiveStatus,
    isCancellationStatus,
} from './file-parser';

export const buildTripKey = (row) => {
    const date = row.journeyDate ? row.journeyDate.toString().trim() : '';
    const service = row.serviceNumber ? row.serviceNumber.toString().trim() : '';
    const route = row.route ? row.route.toString().trim() : '';
    return `${date}__${service || route}`;
};

// Compute the financial breakdown for a single file row per spec:
//   - Fare column is the fare excluding GST. Discount (if any) is
//     subtracted from it to get the net per-seat fare.
//   - gst column carries the total GST for the seat. We split it evenly
//     into CGST + SGST; when gst is zero or absent both stay at zero.
//   - Commission is taken straight from the Commission column.
//   - amount_paid = raw fare − discount + gst (cash the passenger paid).
export const computeSeatFinance = (row) => {
    const rawFare = Number.isFinite(row.fare) ? row.fare : 0;
    const discount = Number.isFinite(row.discount) ? row.discount : 0;
    const fareExGst = Math.max(rawFare - discount, 0);
    const gst = Number.isFinite(row.gst) ? row.gst : 0;
    const cgst = gst > 0 ? gst / 2 : 0;
    const sgst = gst > 0 ? gst / 2 : 0;
    const commission = Number.isFinite(row.commission) ? row.commission : 0;
    const amountPaid = Math.max(rawFare - discount + gst, 0);
    return {
        fareRaw: rawFare,
        discount,
        fare: fareExGst,
        gst,
        cgst,
        sgst,
        totalGst: cgst + sgst,
        ticketCost: fareExGst + cgst + sgst,
        amountPaid,
        commission,
    };
};

export const groupIntoTickets = (rows) => {
    const ticketsByPnr = new Map();
    rows.forEach((row) => {
        const pnr = row.pnr;
        if (!ticketsByPnr.has(pnr)) {
            ticketsByPnr.set(pnr, {
                pnr,
                journeyDate: row.journeyDate,
                route: row.route,
                boardingPoint: row.boardingPoint,
                dropOffPoint: row.dropOffPoint,
                coach: row.coach,
                serviceNumber: row.serviceNumber,
                bookedBy: row.bookedBy,
                branch: row.branch,
                bookedOn: row.bookedOn,
                remarks: row.remarks,
                phoneNumber: row.phoneNumber,
                email: row.email,
                status: row.status,
                serviceStatus: row.serviceStatus,
                category: row.category,
                tripKey: buildTripKey(row),
                travellers: [],
                seats: [],
                missingMandatoryFields: new Set(),
                missingFieldsByRow: [],
                totals: {
                    fareRaw: 0,
                    fare: 0,
                    discount: 0,
                    gst: 0,
                    cgst: 0,
                    sgst: 0,
                    totalGst: 0,
                    ticketCost: 0,
                    amountPaid: 0,
                    commission: 0,
                    netAmount: 0,
                    additionalFare: 0,
                },
                sourceRows: [],
            });
        }
        const ticket = ticketsByPnr.get(pnr);
        ticket.sourceRows.push(row.rowNumber);
        const cleanedSeat = cleanSeatNumber(row.seatNumber);
        const gender = inferGender(row.seatNumber);
        // Mandatory-field check is row-by-row so we can pinpoint exactly
        // which spreadsheet line is incomplete.
        const missingForThisRow = getRowMissingMandatoryFields(row);
        if (missingForThisRow.length > 0) {
            ticket.missingFieldsByRow.push({
                rowNumber: row.rowNumber,
                seatNumber: cleanedSeat,
                missing: missingForThisRow,
            });
            missingForThisRow.forEach((label) => ticket.missingMandatoryFields.add(label));
        }
        // A traveller row is cancelled when Status says Cancel/Cancelled OR
        // the Cancelled On column carries a timestamp.
        const travellerIsCancelled = isCancellationStatus(row.status)
            || isValidString(row.cancelledOn);
        const finance = computeSeatFinance(row);
        ticket.travellers.push({
            fullName: row.name,
            phoneNumber: row.phoneNumber,
            email: row.email,
            gender,
            age: 0,
            seatNumber: cleanedSeat,
            fare: finance.fare,
            netAmount: row.netAmount,
            status: row.status,
            isCancelled: travellerIsCancelled,
            cgst: finance.cgst,
            sgst: finance.sgst,
            totalGst: finance.totalGst,
            commission: finance.commission,
            discount: finance.discount,
            ticketCost: finance.ticketCost,
            amountPaid: finance.amountPaid,
        });
        ticket.seats.push({
            seatNumber: cleanedSeat,
            fare: finance.fare,
            fareRaw: finance.fareRaw,
            discount: finance.discount,
            gst: finance.gst,
            cgst: finance.cgst,
            sgst: finance.sgst,
            totalGst: finance.totalGst,
            ticketCost: finance.ticketCost,
            amountPaid: finance.amountPaid,
            commission: finance.commission,
            ladiesSeat: gender === 'F',
            isActive: isActiveStatus(row.status),
        });
        ticket.totals.fareRaw += finance.fareRaw;
        ticket.totals.fare += finance.fare;
        ticket.totals.discount += finance.discount;
        ticket.totals.gst += finance.gst;
        ticket.totals.cgst += finance.cgst;
        ticket.totals.sgst += finance.sgst;
        ticket.totals.totalGst += finance.totalGst;
        ticket.totals.ticketCost += finance.ticketCost;
        ticket.totals.amountPaid += finance.amountPaid;
        ticket.totals.commission += finance.commission;
        ticket.totals.netAmount += row.netAmount;
        ticket.totals.additionalFare += row.additionalFare;
    });
    // Flatten the per-ticket missingMandatoryFields Set into a stable array.
    return Array.from(ticketsByPnr.values()).map((ticket) => {
        const fieldOrder = MANDATORY_ROW_FIELDS.map((f) => f.label).concat(['Fare']);
        const missingList = fieldOrder.filter((label) => ticket.missingMandatoryFields.has(label));
        return {
            ...ticket,
            missingMandatoryFields: missingList,
        };
    });
};

export const groupIntoTrips = (tickets) => {
    const tripMap = new Map();
    tickets.forEach((ticket) => {
        const key = ticket.tripKey;
        if (!tripMap.has(key)) {
            tripMap.set(key, {
                tripKey: key,
                journeyDate: ticket.journeyDate,
                route: ticket.route,
                serviceNumber: ticket.serviceNumber,
                coach: ticket.coach,
                tickets: [],
                seatsRequired: new Set(),
                seatsCancelled: new Set(),
                activeSeats: new Set(),
                pickupPoints: new Set(),
                dropPoints: new Set(),
                totalFare: 0,
                totalNetAmount: 0,
            });
        }
        const trip = tripMap.get(key);
        trip.tickets.push(ticket);
        trip.totalFare += ticket.totals.fare;
        trip.totalNetAmount += ticket.totals.netAmount;
        if (isValidString(ticket.boardingPoint)) {
            trip.pickupPoints.add(ticket.boardingPoint);
        }
        if (isValidString(ticket.dropOffPoint)) {
            trip.dropPoints.add(ticket.dropOffPoint);
        }
        ticket.seats.forEach((seat) => {
            if (!isValidString(seat.seatNumber)) {
                return;
            }
            trip.seatsRequired.add(seat.seatNumber);
            if (seat.isActive) {
                trip.activeSeats.add(seat.seatNumber);
            } else {
                trip.seatsCancelled.add(seat.seatNumber);
            }
        });
    });
    return Array.from(tripMap.values()).map((trip) => ({
        ...trip,
        seatsRequired: Array.from(trip.seatsRequired),
        seatsCancelled: Array.from(trip.seatsCancelled),
        activeSeats: Array.from(trip.activeSeats),
        pickupPoints: Array.from(trip.pickupPoints),
        dropPoints: Array.from(trip.dropPoints),
    }));
};

// A ticket is "eligible" only when every mandatory file column is populated
// for every row of the PNR.
export const isTicketEligible = (ticket) => (
    !isValidArray(ticket.missingMandatoryFields)
        || ticket.missingMandatoryFields.length === 0
);

// Helper: a ticket is a cancellation when none of its seats are Booked /
// Blocked. We accept either signal — a traveller marked as cancelled OR a
// row whose Status column is Cancel/Cancelled.
export const isCancelledTicket = (ticket) => {
    if (!isValidArray(ticket.travellers) || ticket.travellers.length === 0) {
        return false;
    }
    if (!isValidArray(ticket.seats) || ticket.seats.length === 0) {
        return false;
    }
    if (ticket.seats.some((seat) => seat.isActive)) {
        return false;
    }
    return ticket.travellers.every((t) => t.isCancelled)
        || ticket.travellers.some((t) => t.isCancelled)
        || isCancellationStatus(ticket.status);
};

// Returns the tickets that have at least one mandatory field missing.
export const findIneligibleTickets = (tickets) => {
    if (!isValidArray(tickets)) {
        return [];
    }
    return tickets.filter((t) => (
        isValidArray(t.missingMandatoryFields) && t.missingMandatoryFields.length > 0
    ));
};

// Find tickets in the parsed file that don't fit cleanly into either the
// "to-import" or "to-cancel" buckets. These are rows like seats with no
// recognisable status, or mixed-cancelled PNRs.
export const findIgnoredTickets = (tickets) => {
    if (!isValidArray(tickets)) {
        return [];
    }
    return tickets.filter((ticket) => {
        if (!isValidArray(ticket.seats) || ticket.seats.length === 0) {
            return true;
        }
        const hasActive = ticket.seats.some((seat) => seat.isActive);
        const isCancellation = isCancelledTicket(ticket);
        if (hasActive || isCancellation) {
            return false;
        }
        return true;
    });
};

// Silence unused-import warnings.
void isValidArrayWithValues;
